import { useCallback, useEffect, useMemo, useState } from 'react'
import { If } from 'react-extras'

import { Button, Icon, Popconfirm, Row, Table } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import * as yup from 'yup'

import CustomInput from '../../../../components/CustomInput/customInput'
import CustomSelect from '../../../../components/CustomSelect/customSelect'
import formatYupError from '../../../../util/formatYupError'
import { api, request } from '../../../../util/handlerRequestUtil'
import { getMessage } from '../../../../util/IntlMessages'
import soyAreaTypes from '../../../../util/soyAreaTypes'
import { ConfirmationModal } from '../common'

export function getPaymentMonthKey(paymentMonthYear) {
  if (!paymentMonthYear) {
    return null
  }

  const date = moment(paymentMonthYear)

  if (!date.isValid()) {
    return null
  }

  return date.utc().format('YYYY-MM')
}

const SoyAreaItems = function (props) {
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [technologies, setTechnologies] = useState([])
  const [type, setType] = useState([])
  const [soyAreaTypesById, setSoyAreaTypesById] = useState({})

  const soyAreaItemValidation = useMemo(
    () =>
      yup.object().shape({
        tecnologiaId: yup.string().required(getMessage('mandatoryField')),
        forecastTypeId: yup.string().required(getMessage('mandatoryField')),
        area: yup.number().required(getMessage('mandatoryField')).positive(),
        paymentMonthYear: yup.date().when('forecastTypeId', {
          is: (forecastTypeId) => soyAreaTypesById[forecastTypeId] === soyAreaTypes.RESERVED.description,
          then: (s) => s.required(getMessage('mandatoryField')),
          otherwise: (s) => s.nullable()
        })
      }),
    [soyAreaTypesById]
  )

  useEffect(() => {
    load()
  }, [])

  async function load() {
    await getTecnologias()
    await getForecastsType()
  }

  async function getTecnologias() {
    const items = await request(api.getTechnologiesDropDown({ todos: false }))
    setTechnologies(items)
  }

  async function getForecastsType() {
    const items = await request(api.getForecastsType({ todos: false, active: true }))
    const areaTypesById = items.reduce((prev, curr) => {
      prev[curr.value] = soyAreaTypes[curr.description].description
      return prev
    }, {})
    setType(
      items.map((item) => ({
        value: item.value,
        description: getMessage(`forecast.soy.area.type.${item.description.toLowerCase()}`)
      }))
    )
    setSoyAreaTypesById(areaTypesById)
  }

  const dataSource = useMemo(
    () => props.soyAreaItems.map((item) => ({ ...item, key: item.id })),
    [JSON.stringify(props.soyAreaItems)]
  )

  const columns = [
    {
      title: getMessage('tecnologiaId'),
      dataIndex: 'tecnologiaId',
      key: 'tecnologiaId',
      width: '23%',
      align: 'center',
      render: (value) => technologies.find((t) => t.value === value)?.description
    },
    {
      title: getMessage('forecastTypeId'),
      dataIndex: 'forecastTypeId',
      key: 'forecastTypeId',
      width: '23%',
      align: 'center',
      render: (value) => soyAreaTypesById[value]
    },
    {
      title: getMessage('area'),
      dataIndex: 'area',
      key: 'area',
      width: '23%',
      align: 'center',
      render: (value) => parseInt(value, 10)
    },
    {
      title: getMessage('paymentMonthYear'),
      dataIndex: 'paymentMonthYear',
      key: 'paymentMonthYear',
      width: '23%',
      align: 'center',
      render: (paymentMonthYear) => paymentMonthYear && <span>{moment(paymentMonthYear).utc().format('MM/YYYY')}</span>
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: '7%',
      align: 'center',
      render: (text, record) => (
        <Popconfirm
          title={getMessage('SureToDelete?')}
          onConfirm={() => props.onDeleteItem(record.id)}
          disabled={props.shouldDisableSubmitButton}
        >
          <Icon
            type="delete"
            theme="outlined"
            className="trash-icon"
            style={{
              cursor: props.shouldDisableSubmitButton ? 'not-allowed' : undefined,
              color: props.shouldDisableSubmitButton ? '#cccccc' : undefined
            }}
            aria-disabled={props.shouldDisableSubmitButton}
          />
        </Popconfirm>
      )
    }
  ]

  const handleChange = useCallback(
    (field, value) => {
      setValues((preValues) => ({ ...preValues, [field]: value }))
    },
    [values]
  )

  const handleTypeChange = useCallback(
    (field, value) => {
      handleChange(field, value)
      handleChange('paymentMonthYear', null)
    },
    [values]
  )

  const handleAddItem = useCallback(() => {
    const isReserved = soyAreaTypesById[values.forecastTypeId] === soyAreaTypes.RESERVED.description

    const matchingItem = props.soyAreaItems.find((item) => {
      const sameTechnology = Number(item.tecnologiaId) === Number(values.tecnologiaId)

      const sameForecastType = Number(item.forecastTypeId) === Number(values.forecastTypeId)

      if (!sameTechnology || !sameForecastType) {
        return false
      }

      if (!isReserved) {
        return true
      }

      const currentPaymentMonth = getPaymentMonthKey(item.paymentMonthYear)
      const newPaymentMonth = getPaymentMonthKey(values.paymentMonthYear)

      return currentPaymentMonth === newPaymentMonth
    })

    if (matchingItem) {
      ConfirmationModal(createSoyAreaItem, 'addForecastItems')
    } else {
      createSoyAreaItem()
    }
  }, [values, props.soyAreaItems, soyAreaTypesById])

  function createSoyAreaItem() {
    soyAreaItemValidation
      .validate(values, { abortEarly: false })
      .then(() => {
        props.onAddItem(values)
        setValues({})
        setErrors({})
      })
      .catch((error) => setErrors(formatYupError(error)))
  }

  /**
   * That technology filter is a request by client to hide 'Intacta' from the list of technologies,
   * this is needed to prevent new forecast items to be created with 'Intacta' technology
   */
  const filteredTechnologies = useMemo(
    () => technologies.filter(({ description }) => description.toLowerCase().trim() !== 'intacta') || [],
    [technologies]
  )

  return (
    <Row type="flex" justify="start">
      <CustomSelect
        showSearch
        field="tecnologiaId"
        errors={errors}
        setFieldValue={handleChange}
        rowSpan={4}
        value={values}
        values={filteredTechnologies}
        handleBlur={props.handleBlur}
        required
      />
      <CustomSelect
        showSearch
        field="forecastTypeId"
        errors={errors}
        setFieldValue={handleTypeChange}
        rowSpan={4}
        value={values}
        values={type}
        handleBlur={props.handleBlur}
        required
      />
      <CustomInput
        field="area"
        errors={errors}
        setFieldValue={handleChange}
        rowSpan={4}
        value={values}
        type="number"
        step={0.000001}
        defaultValue={0}
        handleBlur={props.handleBlur}
        required
      />
      <If condition={soyAreaTypesById[values.forecastTypeId] === soyAreaTypes.RESERVED.description}>
        <CustomInput
          disabled={!values.area || values.area === 0}
          type="date"
          field="paymentMonthYear"
          errors={errors}
          setFieldValue={handleChange}
          rowSpan={4}
          value={values}
          format="DD/MM/YYYY"
          handleBlur={props.handleBlur}
          required
        />
      </If>
      <Button
        type="primary"
        className="button-action-plus"
        onClick={handleAddItem}
        disabled={props.shouldDisableSubmitButton}
      >
        <Icon type="plus" />
      </Button>
      <Table bordered className="intacta-reserved-table" columns={columns} dataSource={dataSource} />
    </Row>
  )
}

SoyAreaItems.propTypes = {
  soyAreaItems: PropTypes.array.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired
}

export default SoyAreaItems
